import { useState, useCallback, useEffect } from "react";
import { useUserLocation, useUserActivity } from "./services";
import { useNavigate } from "react-router-dom";
import type { Restaurant } from "../../Manager/restaurant.types";
import YouMightLike from "./Sections/YouMayLike";
import RecentViewed from "./Sections/RecentViewed";
import { Star, AlertCircle, RefreshCw, Wifi, WifiOff } from "lucide-react";
import ByGemio from "./Sections/ByGemio";
import Ranks from "./Sections/Ranks";
import LocalAreas from "./Sections/LocalAreas";
import RestaurantSection from "./Sect";
import { getLocalAreas, getExploreAreas,getCozySpots, getGemioRanks, getRecents, getYouMayLike } from "../../../lib/api/feed.apis";
import { useAuth } from "../../../Context/AuthContext";
import { slugify } from "./services";
import TrendCastBanner from "./Sections/TrendCastBanner";

export interface ExploreData {
  exploreAreas: {
    name: string;
    photos: string[];
  }[]
  exploreRank: {
    city: string;
    restaurants: Restaurant[];
  }
  cozySpots: Restaurant[];
  recentlyViewed: Restaurant[];
  recommendedSpots: Restaurant[];
  localAreas: string[];
}

// Error types
type ErrorType = 'network' | 'server' | 'unknown';

interface ErrorState {
  hasError: boolean;
  type: ErrorType;
  message: string;
}

// Explore Component
const Explore: React.FC = () => {
  const { location, loading: locationLoading, error: locationError } = useUserLocation();
  const { getRecentRestaurantIds, trackClick } = useUserActivity();
  const [loading, setLoading] = useState<boolean>(false);
  const [feedData, setFeedData] = useState<ExploreData | null>(null);
    const [showLocationPopup, setShowLocationPopup] = useState(false);
  const [error, setError] = useState<ErrorState>({
    hasError: false,
    type: 'unknown',
    message: ''
  });
  const [retryCount, setRetryCount] = useState(0);

  const navigate = useNavigate();
  const { user } = useAuth();
  // Determine error type and message
  const handleError = (err: any): ErrorState => {
    // Network error
    if (!navigator.onLine || err.code === 'ERR_NETWORK') {
      return {
        hasError: true,
        type: 'network',
        message: 'No internet connection. Please check your network and try again.'
      };
    }

    // Server error
    if (err.response?.status >= 500) {
      return {
        hasError: true,
        type: 'server',
        message: 'Our servers are having trouble. Please try again in a moment.'
      };
    }

    // Generic error
    return {
      hasError: true,
      type: 'unknown',
      message: 'Something went wrong. Please try again.'
    };
  };
  const isAuthenticated = !!user;
  // Fetch feed data with error handling
  const fetchFeedData = useCallback(async () => {
    if (!navigator.onLine) {
      setError({
        hasError: true,
        type: 'network',
        message: 'You are offline. Please check your internet connection.'
      });
      return;
    }

    setLoading(true);
    setError({ hasError: false, type: 'unknown', message: '' });

    try {
      const recentIds = !isAuthenticated ? getRecentRestaurantIds() : undefined;

      const [
        feedExploreAreas,
        feedGemioRanks,
        feedRecentlyViewed,
        feedYouMayLike,
        feedCozySpots,
        feedLocalAreas,
      ] = await Promise.allSettled([
        getExploreAreas(location.lat, location.lng),
        getGemioRanks(location.lat, location.lng),
        getRecents(location.lat, location.lng, recentIds),
        getYouMayLike(location.lat, location.lng),
        getCozySpots(location.lat, location.lng),
        getLocalAreas(location.lat, location.lng),
      ]);

      const resolve = <T,>(r: PromiseSettledResult<T>, fallback: T): T =>
        r.status === "fulfilled" ? r.value : fallback;

      const emptyRank = { city: "", restaurants: [] as Restaurant[] };

      setFeedData({
        exploreAreas: resolve(feedExploreAreas, []),
        exploreRank: resolve(feedGemioRanks, emptyRank),
        recentlyViewed: resolve(feedRecentlyViewed, []),
        recommendedSpots: resolve(feedYouMayLike, []),
        cozySpots: resolve(feedCozySpots, []),
        localAreas: resolve(feedLocalAreas, []),
      });

      // If every call failed, treat it as an error
      const allFailed = [feedExploreAreas, feedGemioRanks, feedRecentlyViewed, feedYouMayLike, feedCozySpots, feedLocalAreas]
        .every((r) => r.status === "rejected");

      if (allFailed) {
        throw new Error("All feed requests failed");
      }

      setRetryCount(0);
    } catch (err: any) {
      console.error("Error fetching feed data:", err);
      setError(handleError(err));
      setFeedData(null);
    } finally {
      setLoading(false);
    }
  }, [location, isAuthenticated, getRecentRestaurantIds]);

  // Initialize and load feed
  useEffect(() => {
    if (!locationLoading) {
      fetchFeedData();  
    }
  }, [locationLoading, fetchFeedData]);

  // Retry handler
  const handleRetry = () => {
    if (loading) return;
    setRetryCount(prev => prev + 1);
    fetchFeedData();
  };

  // Card click handler
  const handleCardClick = useCallback((id: string, name: string) => {
    if (!id) return;
    trackClick(id.toString());
    navigate(`/restaurant/${id}/${slugify(name)}`);
  }, [trackClick, navigate]);


  // Area click handler
  const handleAreaClick = useCallback((areaName: string) => {
    if (!areaName) return;
    navigate(`/areas/${slugify(areaName)}?name=${encodeURIComponent(areaName)}`);
  }, [navigate]);

  // Render stars function
  const renderStars = (rating: number, size = 10) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <Star key={i} className="fill-[#ff4e1d] text-[#ff4e1d]" size={size} />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <div key={i} className="relative" style={{ width: size, height: size }}>
            <Star className="text-gray-300 absolute" size={size} />
            <div className="overflow-hidden absolute" style={{ width: `${rating / 5 * 100}%` }}>
              <Star className="fill-[#ff4e1d] text-[#ff4e1d]" size={size} />
            </div>
          </div>
        );
      } else {
        stars.push(
          <Star key={i} className="w-3 h-3 text-gray-300" />
        );
      }
    }
    return stars;
  };

  // Error icon based on error type
  const getErrorIcon = () => {
    switch (error.type) {
      case 'network':
        return <WifiOff className="w-16 h-16 text-red-500" />;
      default:
        return <AlertCircle className="w-16 h-16 text-red-500" />;
    }
  };

  // Whenever locationError changes, show popup
  useEffect(() => {
    if (locationError) {
      setShowLocationPopup(true);

      const timer = setTimeout(() => {
        setShowLocationPopup(false);
      }, 5000); // popup visible for 5 seconds

      return () => clearTimeout(timer);
    }
  }, [locationError]);

  // Loading state
  if (locationLoading || (loading && !feedData)) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ff4e1d] mb-4"></div>
          <p className="text-gray-600 text-lg">Loading your personalized feed...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error.hasError && !feedData) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
            <div className="flex justify-center mb-4">
              {getErrorIcon()}
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {error.type === 'network' ? 'No Connection' :  
               'Oops!'}
            </h2>
            <p className="text-gray-600 mb-6">{error.message}</p>
            
            <button
              onClick={handleRetry}
              disabled={loading}
              className="w-full bg-[#ff4e1d] text-white py-3 px-6 rounded-lg font-semibold hover:bg-orange-600 transition disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Retrying...</span>
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" />
                  <span>Try Again</span>
                </>
              )}
            </button>

            {retryCount > 0 && (
              <p className="text-sm text-gray-500 mt-4">
                Retry attempt: {retryCount}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Empty state - no data returned
  if (!feedData || (
    !feedData.exploreAreas?.length && 
    !feedData.recentlyViewed?.length && 
    !feedData.recommendedSpots?.length &&
    !feedData.localAreas?.length
  )) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Oops!
            </h2>
            <p className="text-gray-600 mb-6">
              We couldn't find best for you. Try refreshing or check back later.
            </p>
            
            <button
              onClick={handleRetry}
              className="w-full bg-[#ff4e1d] text-white py-3 px-6 rounded-lg font-semibold hover:bg-orange-600 transition flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  {showLocationPopup && (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg shadow-md z-50 animate-fade-in">
      Showing popular spots. Enable location for nearby recommendations.
    </div>
  )}

  // Success state - render feed
  return (
    <div className="max-w-7xl flex flex-col gap-8 mx-auto">
      {/* Optional: Show retry button if there was a previous error */}
      {error.hasError && feedData && (
        <div className="mx-4 mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <span className="text-sm text-yellow-800">
              Some content may not have loaded properly
            </span>
          </div>
          <button
            onClick={handleRetry}
            className="text-sm text-yellow-800 font-semibold hover:text-yellow-900"
          >
            Retry
          </button>
        </div>
      )}

      {feedData.exploreAreas && feedData.exploreAreas.length > 0 && (
        <YouMightLike 
          loading={loading} 
          cardClick={handleAreaClick} 
          areas={feedData.exploreAreas} 
        />
      )}

      {feedData.recentlyViewed && feedData.recentlyViewed.length > 0 && (
        <RecentViewed 
          onCardClick={handleCardClick}
          loading={loading} 
          renderStars={renderStars} 
          restaurants={feedData.recentlyViewed} 
        />
      )}

      <TrendCastBanner />    

      {feedData.recommendedSpots && feedData.recommendedSpots.length > 0 && (
        <RestaurantSection
          title="Spots you may want to try"
          restaurants={feedData.recommendedSpots}
          onCardClick={handleCardClick}
          loading={loading}
        />
      )}


      {feedData.exploreRank && feedData.exploreRank.restaurants?.length > 0 && (
        <Ranks onCardClick={handleCardClick} rankedCity={feedData.exploreRank} loading={loading} />
      )}

      {feedData.cozySpots && feedData.cozySpots.length > 0 && (
        <RestaurantSection
          title="Dining, Made for Two"
          restaurants={feedData.cozySpots}
          onCardClick={handleCardClick}
          loading={loading}
        />
      )}
      
      <ByGemio loading={loading} />

      {feedData.localAreas && feedData.localAreas.length > 0 && (
        <LocalAreas localAreas={feedData.localAreas} areaClick={handleAreaClick} />
      )}
    </div>
  );
};

export default Explore;